import { useEffect, useState } from 'react';
import { getDoc, getDocs, query, setDoc, where } from 'firebase/firestore';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Typography } from '@mui/material';
import { IconArrowNarrowRight, IconFileLike } from '@tabler/icons-react';
import moment from 'moment';
import { Perfil } from '../../../models/enum';
import Termo from '../../../models/termoAceite';
import VersaoTermo from '../../../models/versaoTermo';
import Aceite from '../../../models/aceite';
import TermoDb from '../../../db/termoAceiteDb';
import VersaoTermoDb from '../../../db/versaoTermoDb';
import AceiteDb from '../../../db/aceiteDb';
import { useUsuario } from '../../../providers/usuarioProvider';

type Pendente = { termo: Termo; versao: VersaoTermo };

async function buscarPendentes(uid: string): Promise<Pendente[]> {
  const snap = await getDocs(query(new TermoDb().collection({ termoId: '' }), where('obrigatorio', '==', true)));
  const aceites = (await getDocs(new AceiteDb().collection({ usuarioId: uid, aceiteId: '' }))).docs.map((d) => d.data());
  const pendentes: Pendente[] = [];
  for (const termo of snap.docs.map((d) => d.data())) {
    if (!termo.versaoVigente) continue;
    const versao = (await getDoc(termo.versaoVigente.withConverter(new VersaoTermoDb().getConverter()))).data();
    if (!versao || versao.situacao !== 'publicado') continue;
    const aceite = aceites.find((item) => item.termo.path === termo.ref.path);
    if (!aceite || aceite.versao.path !== versao.ref.path) pendentes.push({ termo, versao });
  }
  return pendentes;
}

export function useTermosAceitos() {
  const { usuario, claims } = useUsuario();
  const [pendentes, setPendentes] = useState<Pendente[]>([]);
  const [loadingAceite, setLoadingAceite] = useState(true);
  useEffect(() => {
    let ativo = true;
    if (!usuario) {
      setLoadingAceite(false);
      return;
    }
    buscarPendentes(usuario.id)
      .then((v) => {
        if (ativo) {
          setPendentes(v);
          setLoadingAceite(false);
        }
      })
      .catch(() => {
        if (ativo) setLoadingAceite(false);
      });
    return () => {
      ativo = false;
    };
  }, [usuario?.id]);
  const isContadorSemDados =
    claims?.perfil === Perfil.CONTADOR &&
    (!usuario?.dadosEscritorio ||
      !usuario.dadosEscritorio.whatsapp ||
      !usuario.dadosEscritorio.numeroFuncionarios ||
      !usuario.dadosEscritorio.segmento ||
      !usuario.dadosEscritorio.origemLead);
  return { termosAceitos: loadingAceite || pendentes.length === 0, loadingAceite, isContadorSemDados };
}

export default function TermoDeAceite() {
  const { usuario, claims } = useUsuario();
  const [pendentes, setPendentes] = useState<Pendente[]>([]);
  const [loading, setLoading] = useState(true);
  const [check, setCheck] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(false);
  const contadorSemDados =
    claims?.perfil === Perfil.CONTADOR &&
    (!usuario?.dadosEscritorio ||
      !usuario.dadosEscritorio.whatsapp ||
      !usuario.dadosEscritorio.numeroFuncionarios ||
      !usuario.dadosEscritorio.segmento ||
      !usuario.dadosEscritorio.origemLead);
  useEffect(() => {
    let ativo = true;
    if (!usuario) {
      setLoading(false);
      return;
    }
    buscarPendentes(usuario.id)
      .then((v) => {
        if (ativo) {
          setPendentes(v);
          setLoading(false);
        }
      })
      .catch(() => {
        if (ativo) {
          setErro(true);
          setLoading(false);
        }
      });
    return () => {
      ativo = false;
    };
  }, [usuario?.id]);
  const atual = pendentes[0];
  if (loading || !atual || contadorSemDados || erro) return null;
  const aceitar = async () => {
    if (!usuario || !check || salvando) return;
    setSalvando(true);
    const ref = new AceiteDb().doc({ usuarioId: usuario.id, aceiteId: `${atual.termo.ref.id}_${atual.versao.ref.id}` });
    await setDoc(ref, new Aceite({ ref, termo: atual.termo.ref, versao: atual.versao.ref, hash: atual.versao.hash, dataAceite: moment() }), { merge: true });
    setPendentes((v) => v.slice(1));
    setCheck(false);
    setSalvando(false);
  };
  return (
    <Dialog open maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <IconFileLike size={18} />
          <Typography variant="h4">Atualização sobre os Termos de Uso</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Typography color="textSecondary" gutterBottom>
          Leia e aceite a versão vigente para continuar usando o Felix.
        </Typography>
        <Box sx={{ maxHeight: 340, overflow: 'auto', p: 2 }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
            {atual.versao.conteudo}
          </ReactMarkdown>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        <FormControlLabel control={<Checkbox checked={check} onChange={(e) => setCheck(e.target.checked)} />} label="Li e aceito os termos" />
        <Button variant="contained" startIcon={<IconArrowNarrowRight />} disabled={!check || salvando} onClick={aceitar}>
          {salvando ? 'Salvando...' : 'Continuar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
